// src/components/team/QuickStatusForm.jsx
import React, { useState, useEffect } from 'react';
import { 
  Save, 
  X, 
  AlertCircle, 
  CheckCircle, 
  Loader2,
  Users,
  Eye,
  EyeOff,
  History
} from 'lucide-react';
import { QuickStatusButton } from './QuickStatusButton';
import { QuickStatusHistory } from './QuickStatusHistory';
import { statusService } from '../../services/statusService';
import { useAuth } from '../../contexts/AuthContext';

export const QuickStatusForm = ({
  bookId,
  galleryId,
  mediaItem,
  statusConfig,
  onSuccess,
  onCancel,
  bookData
}) => {
  const { user } = useAuth();
  const [selectedStatuses, setSelectedStatuses] = useState({});
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errors, setErrors] = useState({});
  const [showHistory, setShowHistory] = useState(false);

  // Get enabled status types from book settings
  const enabledStatusTypes = Object.entries(bookData?.albumSettings?.quickStatusOptions || {})
    .filter(([key, config]) => config.enabled)
    .map(([key, config]) => ({ key, ...config }));

  useEffect(() => {
    // Load current status from mediaItem
    if (mediaItem) {
      const currentStatuses = {};
      enabledStatusTypes.forEach(({ key }) => {
        const currentStatus = mediaItem[`currentStatus_${key}`];
        if (currentStatus) {
          currentStatuses[key] = currentStatus.statusId;
        }
      });
      setSelectedStatuses(currentStatuses);
    }
  }, [mediaItem]);

  const handleStatusSelect = (statusType, option) => {
    setSelectedStatuses(prev => ({
      ...prev,
      [statusType]: prev[statusType] === option.id ? null : option.id
    }));
    
    // Clear errors for this field
    if (errors[statusType]) {
      setErrors(prev => ({ ...prev, [statusType]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    enabledStatusTypes.forEach(({ key, requireNotes }) => {
      // Check if notes are required when status is set
      if (requireNotes && selectedStatuses[key] && !notes.trim()) {
        newErrors[key] = 'Notes are required for this status type';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Check if any status is selected
    if (Object.keys(selectedStatuses).filter(k => selectedStatuses[k]).length === 0) {
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus(null), 3000);
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Submit each status update
      const promises = Object.entries(selectedStatuses)
        .filter(([_, statusId]) => statusId)
        .map(([statusType, statusId]) => {
          const statusTypeConfig = enabledStatusTypes.find(t => t.key === statusType);
          const option = statusTypeConfig.options.find(o => o.id === statusId);

          return statusService.addStatusUpdate(bookId, galleryId, mediaItem.id, {
            statusType,
            statusId,
            statusLabel: option.label,
            statusIcon: option.icon,
            statusColor: option.color,
            notes: notes.trim(),
            recordedBy: user?.displayName || user?.email || 'Manager',
            recordedByEmail: user?.email,
            metadata: {
              mediaItemTitle: mediaItem.title,
              mediaItemName: mediaItem.metadata?.name
            }
          });
        });

      await Promise.all(promises);

      // Check for wellbeing alerts
      const wellbeingStatus = selectedStatuses['wellbeing'];
      if (wellbeingStatus) {
        const alertThresholds = bookData?.albumSettings?.interactions?.wellbeingAlerts?.triggers;
        if (alertThresholds) {
          const alert = await statusService.checkWellbeingAlerts(
            bookId, 
            galleryId, 
            mediaItem.id, 
            wellbeingStatus,
            alertThresholds
          );
          
          if (alert) {
            console.log('Wellbeing alert triggered:', alert);
            // Handle alert notification (could integrate with notification system)
          }
        }
      }

      setSubmitStatus('success');
      setNotes('');
      
      setTimeout(() => {
        if (onSuccess) onSuccess();
      }, 1500);

    } catch (error) {
      console.error('Error submitting status update:', error);
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus(null), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStatusSection = ({ key, label, icon: Icon, options, requireNotes, showOnCard }) => {
    const selectedStatus = selectedStatuses[key];

    return (
      <div key={key} className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon className="h-5 w-5 text-gray-600" />
            <h4 className="font-medium text-gray-900">{label}</h4>
            {requireNotes && (
              <span className="text-xs text-red-500">*Required notes</span>
            )}
          </div>
          {!showOnCard && (
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <EyeOff className="h-3 w-3" />
              <span>Private</span>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
          {options.map((option) => (
            <QuickStatusButton
              key={option.id}
              option={option}
              isSelected={selectedStatus === option.id}
              onClick={(opt) => handleStatusSelect(key, opt)}
              disabled={isSubmitting}
            />
          ))}
        </div>

        {errors[key] && (
          <p className="text-sm text-red-600 flex items-center gap-1">
            <AlertCircle className="h-4 w-4" />
            {errors[key]}
          </p>
        )}
      </div>
    );
  };

  if (showHistory) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-gray-900">Status History</h3>
          <button
            onClick={() => setShowHistory(false)}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <QuickStatusHistory
          bookId={bookId}
          galleryId={galleryId}
          mediaId={mediaItem.id}
          limit={20}
        />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-bold text-gray-900">Quick Status Update</h3>
          <p className="text-sm text-gray-600 mt-1">
            Update {mediaItem.metadata?.name || mediaItem.title}'s status
          </p>
        </div>
        <button
          onClick={() => setShowHistory(true)}
          className="flex items-center gap-2 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
        >
          <History className="h-4 w-4" />
          History
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Status Sections */}
        {enabledStatusTypes.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p>No status types enabled</p>
            <p className="text-sm mt-1">Enable status tracking in album settings</p>
          </div>
        ) : (
          enabledStatusTypes.map(statusType => renderStatusSection(statusType))
        )}

        {/* Notes Field */}
        {enabledStatusTypes.length > 0 && (
          <div className="space-y-2">
            <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
              Notes {enabledStatusTypes.some(t => t.requireNotes && selectedStatuses[t.key]) && (
                <span className="text-red-500">*</span>
              )}
            </label>
            <textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              placeholder="Add any additional notes or context..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            />
          </div>
        )}

        {/* Submit Status Messages */}
        {submitStatus === 'success' && (
          <div className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
            <CheckCircle className="h-5 w-5" />
            <span>Status updated successfully!</span>
          </div>
        )}

        {submitStatus === 'error' && (
          <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            <AlertCircle className="h-5 w-5" />
            <span>Failed to update status. Please try again.</span>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center gap-3 pt-4 border-t">
          <button
            type="submit"
            disabled={isSubmitting || enabledStatusTypes.length === 0}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                Updating...
              </>
            ) : (
              <>
                <Save className="h-5 w-5" />
                Update Status
              </>
            )}
          </button>
          
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={isSubmitting}
              className="px-4 py-2.5 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors font-medium"
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      {/* Recent Updates Preview */}
      {mediaItem.id && (
        <div className="pt-4 border-t">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Recent Updates</h4>
          <QuickStatusHistory
            bookId={bookId}
            galleryId={galleryId}
            mediaId={mediaItem.id}
            limit={3}
            compact={true}
          />
        </div>
      )}
    </div>
  );
};